'use client';
import * as React from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { ProcessStep } from './ProcessStep';
import { ActionButton } from './ActionButton';
import Image from 'next/image';
import { SectionHeader } from '@/components/ui/layout/SectionHeader';
import job from '@/assets/images/job-approach.png';
import coffee from '@/assets/images/coffee.webp';
import { getActiveLocale } from '@/utils/active-locale';
import { useRouter } from 'next/navigation';

interface VisualGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dictionary: any;
}

export const VisualGrid: React.FC<VisualGridProps> = ({ dictionary }) => {
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const activeLocale = getActiveLocale();
  const navigate = useRouter();
  const approachHandler = () => {
    navigate.push('/' + activeLocale + '/voor-talenten/onze-aanpak');
  };

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Gebruik useScroll met een ref zodat we de scrollprogressie van de container krijgen.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Map de scrollprogressie (0 tot 1) naar een horizontale translatie van 40px naar 0.
  const xTransform = useTransform(scrollYProgress, [0, 1], [80, -20]);
  // Voeg een spring toe voor een vloeiende animatie.
  const xSpring = useSpring(xTransform, { stiffness: 117, damping: 30 });

  // Pas het effect alleen toe op mobiele schermen.
  const finalX = isMobile ? xSpring : 0;

  return (
    <motion.div ref={containerRef} style={{ x: finalX }} className='flex relative justify-between gap-x-4 items-end w-full font-bold max-md:max-w-full'>
      <div className='flex flex-col gap-y-7'>
        <Image src={job} alt='Job, recruiter bij Teelor' width={197} height={197} className='object-contain z-0 shrink-0 aspect-square rounded-[48px] min-w-[197px] scale-x-[-1]' />
        <ProcessStep number={1} title={dictionary.home.meet} />
      </div>
      <div className='flex flex-col gap-y-7'>
        <div className='flex z-0 shrink-0 bg-yellow-600 h-[529px] rounded-[48px] w-[197px]' />
        <ProcessStep number={2} title={dictionary.home.match} />
      </div>
      <div className='flex flex-col gap-y-7'>
        <div className='flex z-0 shrink-0 bg-violet-300 h-[346px] rounded-[48px] w-[197px]' />
        <ProcessStep number={3} title={dictionary.home.support} />
      </div>
      <div className='flex flex-col gap-y-7'>
        <article className='z-0 text-base leading-6 text-grey-600 w-[198px]'>
          <p>{dictionary.home.no_luck}</p>
          <div className='flex mt-11 bg-purple-200 h-[195px] min-h-[191px] rounded-[48px] w-[195px] max-md:mt-10' />
        </article>
        <ProcessStep number={4} title={dictionary.home.stay} />
      </div>
      <div className='flex flex-col items-end gap-y-[88px] mb-[84px]'>
        <Image src={coffee} width={197} height={1} alt='Coffee Teelor kantoor Rotterdam' className='object-contain z-0 shrink-0 aspect-[0.62] rounded-[48px] min-w-[197px]' />
        <div className='w-fit z-10'>
          <ActionButton label={dictionary.home.our_approach} onClick={approachHandler} />
        </div>
      </div>
      <SectionHeader content={dictionary.home.no_gamble} textColor='text-dark-900' className='absolute left-0 z-0 gap-7 self-start min-w-60 top-[53px] w-[425px] max-md:max-w-full' />
      <div className='absolute h-[0.5px] border-b left-0 right-0 bottom-[104px] border-dashed border-grey-300' />
    </motion.div>
  );
};
